/**
 * Dates, times and numbers as the CRM prints them, in the reader's UI language.
 *
 * Times of day are printed in the PRACTICE's time zone when the server states one
 * (`today.timezone`), so "10:00 Priya" is 10:00 on the practitioner's own clock whatever
 * the browser's zone. Everything else uses the browser's zone.
 */
import { intlLocaleFor } from "../../lib/formatMoney";

const locale = (lang) => intlLocaleFor(lang);
const valid = (iso) => {
  const d = iso instanceof Date ? iso : new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** "10:00" */
export const fmtTime = (iso, lang, timeZone) => {
  const d = valid(iso);
  if (!d) return "";
  return new Intl.DateTimeFormat(locale(lang), { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone }).format(d);
};

/** "Tue" */
export const fmtWeekdayShort = (iso, lang, timeZone) => {
  const d = valid(iso);
  return d ? new Intl.DateTimeFormat(locale(lang), { weekday: "short", timeZone }).format(d).replace(/\.$/, "") : "";
};

/** "Sat 21:40" (consent evidence). */
export const fmtWeekdayTime = (iso, lang, timeZone) => {
  const d = valid(iso);
  return d ? `${fmtWeekdayShort(d, lang, timeZone)} ${fmtTime(d, lang, timeZone)}` : "";
};

/** "25 Sep" */
export const fmtDayMonth = (iso, lang, timeZone) => {
  const d = valid(iso);
  return d ? new Intl.DateTimeFormat(locale(lang), { day: "numeric", month: "short", timeZone }).format(d).replace(/\.$/, "") : "";
};

/** "25 Sep 2026" */
export const fmtDate = (iso, lang) => {
  const d = valid(iso);
  return d ? new Intl.DateTimeFormat(locale(lang), { day: "numeric", month: "short", year: "numeric" }).format(d) : "";
};

/** "Jan 2026" (Member since). */
export const fmtMonthYear = (iso, lang) => {
  const d = valid(iso);
  return d ? new Intl.DateTimeFormat(locale(lang), { month: "short", year: "numeric" }).format(d).replace(/\.(?=\s)/, "") : "";
};

/** "14 Mar" from a `{day, month}` birthday (no year needed, per spec). */
export const fmtBirthday = (birthday, lang) => {
  if (!birthday || !birthday.day || !birthday.month) return "";
  const d = new Date(Date.UTC(2000, birthday.month - 1, birthday.day));
  return new Intl.DateTimeFormat(locale(lang), { day: "numeric", month: "short", timeZone: "UTC" }).format(d).replace(/\.$/, "");
};

/** Month names for the birthday picker, 1..12. */
export const monthNames = (lang) => Array.from({ length: 12 }, (_, i) =>
  new Intl.DateTimeFormat(locale(lang), { month: "long", timeZone: "UTC" }).format(new Date(Date.UTC(2000, i, 1))));

const capitalise = (s) => (s ? s.charAt(0).toLocaleUpperCase() + s.slice(1) : s);
const DAY = 86400000;

/**
 * The wireframe's right-aligned feed date: "Today", "Yesterday", "3 days ago" within a
 * week, then "25 Sep" within the year, then "Jan 2025".
 */
export const fmtRelative = (iso, now, lang) => {
  const d = valid(iso);
  if (!d) return "";
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(d) - startOf(now)) / DAY);
  if (days <= 0 && days > -7) {
    return capitalise(new Intl.RelativeTimeFormat(locale(lang), { numeric: "auto" }).format(days, "day"));
  }
  if (d.getFullYear() === now.getFullYear()) return fmtDayMonth(d, lang);
  return fmtMonthYear(d, lang);
};

/** "1,187" in the reader's grouping. */
export const fmtCount = (n, lang) => new Intl.NumberFormat(locale(lang)).format(Number(n) || 0);

/** "61%" — a sequence's open rate (opened / sent), rounded; `null` when there is nothing sent yet. */
export const fmtOpenRate = (sent, opened, lang) => {
  if (!sent) return null;
  return new Intl.NumberFormat(locale(lang), { style: "percent", maximumFractionDigits: 0 }).format((Number(opened) || 0) / sent);
};

/** "2026-09-29" in a given zone (the key the server uses for "today"). */
export const dayKey = (iso, timeZone) => {
  const d = valid(iso);
  if (!d) return "";
  const parts = new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return `${get("year")}-${get("month")}-${get("day")}`;
};
